'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Component } from 'react';
import PropTypes from 'prop-types';
import I18n from 'I18n';
import { FORM_FIELD_TYPES } from 'MeetingsBase/constants/enums';
import UIList from 'UIComponents/list/UIList';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UINumberInput from 'UIComponents/input/UINumberInput';
import UIRadioInput from 'UIComponents/input/UIRadioInput';
import UICheckbox from 'UIComponents/input/UICheckbox';
import UITextInput from 'UIComponents/input/UITextInput';
import UITextArea from 'UIComponents/input/UITextArea';
import UISelectInput from 'UIComponents/input/UISelectInput';
import UIToggleGroup from 'UIComponents/input/UIToggleGroup';
import UIDateInput from 'UIComponents/dates/UIDateInput';
import { fromMoment, toMomentUTC } from 'UIComponents/core/SimpleDate';
import partial from 'react-utils/partial';
const EMAIL_FIELD = 'email';
const MINUS_SIGN = '\u2212';
const INVALID_NUM_CHARS_REGEX = new RegExp(`[^0-9.${MINUS_SIGN}-]`, 'g');
const INVALID_DASHES_REGEX = new RegExp(`(?!^-)-`, 'g');
const INVALID_MINUS_SIGN_REGEX = new RegExp(`(?!^${MINUS_SIGN})${MINUS_SIGN}`, 'g');
const WHITE_SPACE_REGEX = /\s/g;

class BookingFormField extends Component {
  constructor(_props) {
    super(_props);

    this.isNameField = () => {
      return ['firstName', 'lastName'].includes(this.props.fieldName);
    };

    this.getEnumOptions = options => {
      return options.map(option => {
        return {
          text: option.label,
          value: option.value
        };
      });
    };

    this.onFieldBlur = () => {
      this.setState({
        hasBeenBlurred: true
      });
    };

    this.handleUnformattedNumberBlur = (fieldName, e) => {
      // Ignore extraneous characters to mimic original UINumberInput behavior
      const validValue = e.target.value.replace(INVALID_NUM_CHARS_REGEX, '').replace(INVALID_DASHES_REGEX, '').replace(INVALID_MINUS_SIGN_REGEX, '');
      this.props.setFormValue(fieldName, validValue);
      this.onFieldBlur();
    };

    this.handlePhoneNumberFieldBlur = (e, fieldName) => {
      let validValue = e.target.value;
      validValue = validValue.replace(WHITE_SPACE_REGEX, '');
      this.props.setFormValue(fieldName, validValue);
      this.onFieldBlur();
    };

    this.handleEnumValueChange = ({
      fieldName,
      value
    }) => {
      this.props.setFormValue(fieldName, value);
    };

    this.handleBooleanCheckboxValueChange = e => {
      this.props.setFormValue(this.props.fieldName, e.target.checked);
    };

    this.handleValueChange = (fieldName, e) => {
      let value = e.target.value;

      if (fieldName === EMAIL_FIELD) {
        value = value.trim();
      }

      this.props.setFormValue(fieldName, value);
    };

    this.handleDateValueChange = (fieldName, e) => {
      this.props.setFormValue(fieldName, toMomentUTC(e.target.value).valueOf());
    };

    this.renderStandardField = FormFieldComponent => {
      const {
        fieldName,
        fieldValue,
        autocomplete,
        placeholder,
        disabled,
        fieldType
      } = this.props;

      const onBlur = e => {
        if (fieldType === FORM_FIELD_TYPES.PHONE_NUMBER) {
          return this.handlePhoneNumberFieldBlur(e, fieldName);
        }

        return this.onFieldBlur();
      };

      return /*#__PURE__*/_jsx(FormFieldComponent, {
        style: {
          maxWidth: '100%'
        },
        autoComplete: autocomplete,
        type: "text",
        name: fieldName,
        className: `confirm-${fieldName}-field`,
        value: fieldValue || '',
        onChange: partial(this.handleValueChange, fieldName),
        onBlur: onBlur,
        placeholder: placeholder,
        disabled: disabled
      });
    };

    this.renderTextField = () => {
      return this.renderStandardField(UITextInput);
    };

    this.renderTextAreaField = () => {
      return this.renderStandardField(UITextArea);
    };

    this.renderEnumType = renderFunction => {
      const {
        fieldOptions
      } = this.props;
      return /*#__PURE__*/_jsx(UIList, {
        children: fieldOptions.map(option => {
          if (option.value !== '') {
            return renderFunction(option);
          }

          return null;
        })
      });
    };

    this.renderCheckbox = () => {
      const {
        fieldValue,
        fieldLabel,
        isRequired,
        fieldName,
        disabled
      } = this.props;
      return /*#__PURE__*/_jsxs(UICheckbox, {
        className: "m-top-6",
        checked: fieldValue,
        onChange: this.handleBooleanCheckboxValueChange,
        onBlur: partial(this.onFieldBlur, {
          target: {
            name: fieldName
          }
        }),
        disabled: disabled,
        children: [fieldLabel, isRequired && /*#__PURE__*/_jsx("span", {
          children: " * "
        })]
      });
    };

    this.renderRadioInput = option => {
      const {
        fieldName,
        fieldValue,
        disabled
      } = this.props;
      return /*#__PURE__*/_jsx(UIRadioInput, {
        name: `${fieldName}-${option.value}`,
        checked: option.value === fieldValue,
        disabled: disabled,
        onChange: partial(this.handleEnumValueChange, {
          fieldName,
          value: option.value
        }),
        onBlur: partial(this.onFieldBlur, {
          target: {
            name: fieldName
          }
        }),
        children: option.label
      }, `${fieldName}-${option.value}`);
    };

    this.renderSelectField = () => {
      const {
        fieldName,
        fieldType,
        fieldOptions,
        fieldValue,
        setFormValue,
        placeholder
      } = this.props;
      const placeholderText = I18n.text('select') || placeholder;

      if (fieldType === FORM_FIELD_TYPES.CHECKBOX) {
        return /*#__PURE__*/_jsx(UIToggleGroup, {
          name: fieldName,
          children: fieldOptions.map(option => {
            const onChange = ({
              target: {
                checked
              }
            }) => {
              if (checked) {
                setFormValue(fieldName, [...fieldValue, option.value]);
              } else {
                setFormValue(fieldName, fieldValue.filter(value => value !== option.value));
              }
            };

            return {
              onChange,
              checked: fieldValue.includes(option.value),
              key: option.value,
              label: option.label
            };
          }).map(option => {
            const {
              key,
              label
            } = option,
                  props = _objectWithoutPropertiesLoose(option, ["key", "label"]);

            return /*#__PURE__*/_jsx(UICheckbox, Object.assign({}, props, {
              children: label
            }), key);
          })
        });
      } else {
        // Do not switch to UISelect despite deprecation warning in UILibrary
        // See https://git.hubteam.com/HubSpot/MeetingsUI/pull/1446
        return /*#__PURE__*/_jsx(UISelectInput, {
          name: fieldName,
          onChange: partial(this.handleValueChange, fieldName),
          onBlur: this.onFieldBlur,
          options: this.getEnumOptions(fieldOptions),
          placeholder: placeholderText,
          value: fieldValue || undefined
        });
      }
    };

    this.renderEnumerationFields = () => {
      const {
        fieldType
      } = this.props;

      switch (fieldType) {
        case FORM_FIELD_TYPES.BOOLEAN_CHECKBOX:
          return this.renderCheckbox();

        case FORM_FIELD_TYPES.RADIO:
          return this.renderEnumType(this.renderRadioInput);

        default:
          return this.renderSelectField();
      }
    };

    this.renderUnformattedNumberField = () => {
      const {
        fieldName,
        fieldValue
      } = this.props;
      return /*#__PURE__*/_jsx(UITextInput, {
        name: fieldName,
        onChange: partial(this.handleValueChange, fieldName),
        onBlur: partial(this.handleUnformattedNumberBlur, fieldName),
        value: fieldValue
      });
    };

    this.renderNumberField = () => {
      const {
        fieldName,
        fieldValue,
        numberDisplayHint
      } = this.props;

      if (numberDisplayHint === 'unformatted') {
        return this.renderUnformattedNumberField();
      } else {
        return /*#__PURE__*/_jsx(UINumberInput, {
          name: fieldName,
          onChange: partial(this.handleValueChange, fieldName),
          onBlur: this.onFieldBlur,
          value: fieldValue
        });
      }
    };

    this.renderDateField = () => {
      const {
        fieldName,
        fieldValue
      } = this.props;
      let dateValue;

      if (fieldValue) {
        const utcFieldValue = I18n.moment.utc(fieldValue);
        dateValue = isNaN(utcFieldValue) ? null : fromMoment(utcFieldValue);
      }

      return /*#__PURE__*/_jsx("div", {
        className: "meetings-date-input",
        children: /*#__PURE__*/_jsx(UIDateInput, {
          placeholder: I18n.moment.localeData().longDateFormat('L') || 'MM/DD/YYYY',
          value: dateValue,
          onChange: partial(this.handleDateValueChange, fieldName),
          onBlur: this.onFieldBlur
        })
      });
    };

    this.renderInput = () => {
      const {
        fieldIsCustom,
        type,
        fieldType
      } = this.props;

      switch (true) {
        case type === FORM_FIELD_TYPES.ENUMERATION:
          return this.renderEnumerationFields();

        case type === FORM_FIELD_TYPES.NUMBER:
          return this.renderNumberField();

        case type === FORM_FIELD_TYPES.DATE:
          return this.renderDateField();

        case fieldIsCustom || fieldType === FORM_FIELD_TYPES.TEXTAREA:
          return this.renderTextAreaField();

        default:
          return this.renderTextField();
      }
    };

    this.state = {
      hasBeenBlurred: false
    };
  }

  componentDidMount() {
    const {
      fieldType,
      fieldName,
      fieldValue,
      setFormValue
    } = this.props;

    if (fieldType === FORM_FIELD_TYPES.CHECKBOX) {
      if (fieldValue == null) {
        setFormValue(fieldName, []);
      } else if (typeof fieldValue === 'string') {
        setFormValue(fieldName, fieldValue.split(';'));
      }
    }

    if (fieldType === FORM_FIELD_TYPES.DATE && fieldValue && typeof fieldValue === 'string') {
      // If a fieldValue for a date field is passed as a query param, it has to be in a YYYY-MM-DD string
      const utcFieldValue = I18n.moment.utc(fieldValue);
      const timestampFromFieldValue = isNaN(utcFieldValue) ? null : toMomentUTC(fromMoment(utcFieldValue)).valueOf();

      if (timestampFromFieldValue) {
        setFormValue(fieldName, timestampFromFieldValue);
      }
    }
  }

  render() {
    const {
      validation,
      isRequired,
      fieldLabel,
      fieldName,
      fieldType
    } = this.props;
    const hasError = Boolean(validation) && this.state.hasBeenBlurred;
    let validationMessage;

    if (hasError) {
      validationMessage = validation;
    }

    const classes = !this.isNameField() ? 'm-top-6' : "";
    const isBooleanCheckbox = fieldType === FORM_FIELD_TYPES.BOOLEAN_CHECKBOX;
    return /*#__PURE__*/_jsx(UIFormControl, {
      "data-selenium-test": `booking-form-field-${fieldName}`,
      "data-test-id": "booking-form-field",
      className: classes,
      label: !isBooleanCheckbox && fieldLabel,
      required: isRequired,
      error: hasError,
      validationMessage: validationMessage,
      onBlur: this.onFieldBlur,
      children: this.renderInput()
    });
  }

}

BookingFormField.propTypes = {
  fieldValue: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.object, PropTypes.number, PropTypes.bool]),
  fieldName: PropTypes.string.isRequired,
  validation: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  setFormValue: PropTypes.func.isRequired,
  autocomplete: PropTypes.string,
  fieldType: PropTypes.string,
  type: PropTypes.string,
  isRequired: PropTypes.bool,
  fieldLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  fieldOptions: PropTypes.array,
  fieldIsCustom: PropTypes.bool,
  placeholder: PropTypes.string,
  numberDisplayHint: PropTypes.string,
  disabled: PropTypes.bool
};
BookingFormField.defaultProps = {
  autocomplete: 'off'
};
export default BookingFormField;